import { useEffect, useRef, useState } from 'react';
import { X, Download, FileText, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useCanvasStore } from './canvas-store';

function ReportMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '24px 0 12px', color: '#111827' }}>{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: '20px 0 8px', color: '#1f2937' }}>{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 6px', color: '#374151' }}>{children}</h3>
        ),
        h4: ({ children }) => (
          <h4 style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 5px', color: '#4b5563' }}>{children}</h4>
        ),
        strong: ({ children }) => (
          <strong style={{ fontWeight: 700, color: '#111827' }}>{children}</strong>
        ),
        em: ({ children }) => (
          <em style={{ fontStyle: 'italic' }}>{children}</em>
        ),
        blockquote: ({ children }) => (
          <blockquote style={{ borderLeft: '3px solid #d1d5db', paddingLeft: 12, margin: '8px 0', color: '#6b7280' }}>{children}</blockquote>
        ),
        ul: ({ children }) => (
          <ul style={{ paddingLeft: 20, margin: '6px 0', listStyleType: 'disc' }}>{children}</ul>
        ),
        ol: ({ children }) => (
          <ol style={{ paddingLeft: 20, margin: '6px 0', listStyleType: 'decimal' }}>{children}</ol>
        ),
        li: ({ children }) => (
          <li style={{ margin: '3px 0', paddingLeft: 4, lineHeight: 1.7 }}>{children}</li>
        ),
        code: ({ children }) => (
          <code style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px', fontSize: 12, fontFamily: 'monospace', color: '#374151' }}>{children}</code>
        ),
        p: ({ children }) => (
          <p style={{ margin: '6px 0', lineHeight: 1.75 }}>{children}</p>
        ),
        hr: () => (
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />
        ),
        table: ({ children }) => (
          <div style={{ overflowX: 'auto', margin: '12px 0' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead style={{ background: '#f9fafb' }}>{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody>{children}</tbody>
        ),
        tr: ({ children }) => (
          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>{children}</tr>
        ),
        th: ({ children }) => (
          <th style={{ padding: '8px 12px', fontWeight: 600, color: '#111827', textAlign: 'left', border: '1px solid #e5e7eb' }}>{children}</th>
        ),
        td: ({ children }) => (
          <td style={{ padding: '8px 12px', color: '#374151', border: '1px solid #e5e7eb', verticalAlign: 'top' }}>{children}</td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export function DocumentOverlay() {
  const { showDocumentOverlay, documentOverlayContent, documentOverlayTitle, closeDocumentOverlay } = useCanvasStore();
  const persistenceProjectId = useCanvasStore((s) => s.activeProjectId);
  const researchTasksByConversation = useCanvasStore((s) => s.researchTasksByConversation);
  const convKey: string | number = persistenceProjectId ?? 'new';
  const _rawResearchTask = researchTasksByConversation[convKey];
  const activeResearchTask = _rawResearchTask && !_rawResearchTask.dismissed ? _rawResearchTask : null;
  const [copied, setCopied] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDocumentOverlay) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDocumentOverlay();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showDocumentOverlay, closeDocumentOverlay]);

  const handleLoadFromServer = async () => {
    if (!activeResearchTask?.taskId || documentOverlayContent) return;
    try {
      const resp = await fetch(`/api/canvas/tasks/${activeResearchTask.taskId}/document`);
      if (resp.ok) {
        const data = await resp.json();
        useCanvasStore.getState().openDocumentOverlay(data.title, data.document);
      } else {
        useCanvasStore.getState().openDocumentOverlay(
          'Research Report',
          'Report generation failed. Try running the research again.'
        );
      }
    } catch {
      useCanvasStore.getState().openDocumentOverlay(
        'Research Report',
        'Report generation failed. Try running the research again.'
      );
    }
  };

  useEffect(() => {
    if (showDocumentOverlay && !documentOverlayContent && activeResearchTask?.documentReady) {
      handleLoadFromServer();
    }
  }, [showDocumentOverlay]);

  if (!showDocumentOverlay) return null;

  const content = documentOverlayContent || '';
  const title = documentOverlayTitle || 'Research Report';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      data-testid="document-overlay-backdrop"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        ref={overlayRef}
        data-testid="document-overlay-panel"
        style={{
          background: 'var(--color-background-primary, #ffffff)',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border-tertiary, #e5e7eb)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--color-background-primary, #ffffff)',
            flexShrink: 0,
          }}
        >
          <FileText size={16} style={{ color: 'var(--color-text-secondary, #6b7280)', flexShrink: 0 }} />
          <span
            data-testid="text-document-title"
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: 'var(--color-text-primary, #111827)',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              data-testid="btn-download-document"
              onClick={handleDownload}
              title="Download as Markdown"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 10px',
                border: '1px solid var(--color-border-tertiary, #e5e7eb)',
                borderRadius: 6,
                background: 'var(--color-background-primary, #ffffff)',
                cursor: 'pointer',
                fontSize: 12,
                color: 'var(--color-text-secondary, #6b7280)',
                fontWeight: 500,
              }}
            >
              <Download size={12} />
              Download MD
            </button>
            {copied && (
              <span style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Check size={12} /> Copied
              </span>
            )}
            <button
              data-testid="btn-close-document"
              onClick={closeDocumentOverlay}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                border: 'none',
                borderRadius: 6,
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--color-text-tertiary, #9ca3af)',
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          data-testid="document-overlay-content"
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '24px 28px',
            lineHeight: 1.7,
            fontSize: 14,
            color: 'var(--color-text-primary, #374151)',
          }}
        >
          {content ? (
            <ReportMarkdown content={content} />
          ) : (
            <p style={{ color: '#9ca3af', textAlign: 'center', marginTop: 48 }}>Loading report...</p>
          )}
        </div>
      </div>
    </div>
  );
}
